import Workspace, { WorkspaceDefaultModule, WorkspaceFramework } from '../types/Workspace';

export const getDefaultModuleOptions = (framework: WorkspaceFramework): WorkspaceDefaultModule[] => {
  const isTriageArchiveEnabled = import.meta.env.VITE_FEATURE_TRIAGE_ARCHIVE === 'true';
  const triageOption = isTriageArchiveEnabled ? [WorkspaceDefaultModule.triage] : [];
  const archiveOption = isTriageArchiveEnabled ? [WorkspaceDefaultModule.archive] : [];
  switch (framework) {
    case WorkspaceFramework.scrum:
      return [
        ...triageOption,
        WorkspaceDefaultModule.backlog,
        WorkspaceDefaultModule.planning,
        WorkspaceDefaultModule.sprint,
        ...archiveOption,
      ];
    case WorkspaceFramework.kanban:
      return [...triageOption, WorkspaceDefaultModule.backlog, WorkspaceDefaultModule.kanban, ...archiveOption];
    case WorkspaceFramework.none:
      return [...triageOption, WorkspaceDefaultModule.backlog, ...archiveOption];
    default:
      return [WorkspaceDefaultModule.backlog];
  }
};

export const getDefaultModuleDisplayName = (module: WorkspaceDefaultModule): string => {
  switch (module) {
    case WorkspaceDefaultModule.triage:
      return 'Triage';
    case WorkspaceDefaultModule.backlog:
      return 'Backlog';
    case WorkspaceDefaultModule.planning:
      return 'Planning';
    case WorkspaceDefaultModule.sprint:
      return 'Sprint';
    case WorkspaceDefaultModule.kanban:
      return 'Kanban';
    case WorkspaceDefaultModule.archive:
      return 'Archive';
    default:
      return 'Backlog';
  }
};

export const getEffectiveDefaultModule = (space: Workspace): WorkspaceDefaultModule => {
  if (space.defaultModule) {
    const validOptions = getDefaultModuleOptions(space.framework);
    if (validOptions.includes(space.defaultModule)) {
      return space.defaultModule;
    }
  }
  return WorkspaceDefaultModule.backlog;
};

export const getSpaceDefaultRoute = (orgPublicId: string | undefined, space: Workspace): string | null => {
  if (!orgPublicId) return null;
  const defaultModule = getEffectiveDefaultModule(space);
  return `/${orgPublicId}/space/${space.publicId}/${defaultModule}`;
};

export type NoSpaceDestination = 'wait' | 'join-space' | 'new-space';

// Where to send a user who has no spaces yet: offer public spaces to join when any
// are discoverable, otherwise fall back to creating a space. Returns 'wait' until the
// discoverable query has settled so a still-loading result never routes to 'new-space'.
export const chooseNoSpaceDestination = (args: {
  discoverableSettled: boolean;
  discoverableCount: number;
}): NoSpaceDestination => {
  if (!args.discoverableSettled) return 'wait';
  return args.discoverableCount > 0 ? 'join-space' : 'new-space';
};

export type OrgUrlResolution<T> =
  | { status: 'wait' }
  | { status: 'no-access' }
  | { status: 'resolved'; organization: T };

// Resolves the org in a `/:orgPublicId/...` route against the user's access list. An
// access list that has not loaded yet is not proof the user lacks access — treating it
// as such is what sent a freshly onboarded member to the org picker instead of the space
// they had just created (#2987). Callers wait on 'wait' and re-run once the list settles.
export const resolveOrgFromUrl = <T extends { publicId: string }>(
  orgPublicId: string,
  organizations: T[] | undefined,
  organizationsSettled: boolean
): OrgUrlResolution<T> => {
  const organization = organizations?.find((org) => org.publicId === orgPublicId);
  if (organization) return { status: 'resolved', organization };
  return organizationsSettled ? { status: 'no-access' } : { status: 'wait' };
};
